/**
 * Academy domain — `AcademyLearningStore` state shape. Remote-only, in-memory.
 */
import type { Guid } from './academy-enums.model';
import type {
  CourseResponse,
  LearningPathCourseResponse,
  LearningPathResponse,
} from './academy.model';
import type {
  CourseAttachmentResponse,
  CourseItemUploadResponse,
  CourseProgressResponse,
  MineResult,
  MyCourseSummary,
  MyLearningPathSummary,
  PathProgressResponse,
} from './academy-responses.model';
import type { CourseItemEntity } from './academy-entities.model';

export interface AcademyLearningState {
  /** Courses by id (discover results and/or detail). */
  coursesById: Record<Guid, CourseResponse>;
  /** Order of the last course discovery. */
  courseIds: Guid[];
  /** Order of the last producer-side OData list (drafts included, unlike discover). */
  producerCourseIds: Guid[];
  /** Course items via `$expand=Items` (§6.4), keyed by course id and sorted by Order. */
  itemsByCourse: Record<Guid, CourseItemEntity[]>;
  /** Support materials per course (`GET /Courses/{id}/attachments`), keyed by course id. */
  attachmentsByCourse: Record<Guid, CourseAttachmentResponse[]>;
  /** Direct-upload progress (0..100) while a VOD binary streams to Cloudflare. */
  uploadProgress: number;
  /** Last completed upload handshake (item/event ids) — null until an upload lands. */
  lastUpload: CourseItemUploadResponse | null;
  /** Learning paths by id. */
  pathsById: Record<Guid, LearningPathResponse>;
  /** Order of the last path discovery. */
  pathIds: Guid[];
  /** Order of the last producer-side OData list (drafts included, unlike discover). */
  producerPathIds: Guid[];
  /** A path's ordered course links (`GET /OData/LearningPathCourses`), keyed by path id. */
  pathCoursesByPath: Record<Guid, LearningPathCourseResponse[]>;
  /** Per-course progress for the current viewer. */
  courseProgressById: Record<Guid, CourseProgressResponse>;
  /** Per-path progress for the current viewer. */
  pathProgressById: Record<Guid, PathProgressResponse>;
  /** Course-item playback tokens, keyed `item:{courseId}:{itemId}`. */
  playbackTokenByKey: Record<string, string>;
  /** Student-view course summaries from `GET /Courses/mine` (progress, status, cover). */
  myCourses: MyCourseSummary[];
  /** Server-reported total for `GET /Courses/mine` (for pagination). */
  myCoursesTotal: number;
  /** Student-view path summaries from `GET /LearningPaths/mine`. */
  myPaths: MyLearningPathSummary[];
  /** Server-reported total for `GET /LearningPaths/mine` (for pagination). */
  myPathsTotal: number;
}

export const initialAcademyLearningState: AcademyLearningState = {
  coursesById: {},
  courseIds: [],
  producerCourseIds: [],
  itemsByCourse: {},
  attachmentsByCourse: {},
  uploadProgress: 0,
  lastUpload: null,
  pathsById: {},
  pathIds: [],
  producerPathIds: [],
  pathCoursesByPath: {},
  courseProgressById: {},
  pathProgressById: {},
  playbackTokenByKey: {},
  myCourses: [],
  myCoursesTotal: 0,
  myPaths: [],
  myPathsTotal: 0,
};

// Re-export for convenience in store methods that receive paginated results.
export type { MineResult };
